import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Upload, X, Image, Play } from 'lucide-react';
import videoService, { VideoDoc } from '../../services/videoService';
import ImagePreviewModal from '../audio/ImagePreviewModal';
import VideoPreviewModal from './VideoPreviewModal';

interface VideoFormData {
  draftId: string;
  title: string;
  description: string;
  category: 'longvideo' | 'short';
  youtubeUrl: string;
  thumbnailFile?: File | null;
  thumbnailUrl?: string;
}

interface VideoFormProps {
  video?: VideoDoc | null;
  onSubmit: (success: boolean) => void;
  onCancel: () => void;
  loading: boolean;
  readOnly?: boolean;
}

const VideoForm: React.FC<VideoFormProps> = ({ video, onSubmit, onCancel, loading, readOnly = false }) => {
  const [draftId, setDraftId] = useState<string | null>(null);
  const isDraftCreatedRef = useRef(false);

  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailUrl, setThumbnailUrl] = useState<string>('');

  // Preview modals
  const [showImagePreview, setShowImagePreview] = useState(false);
  const [previewImageFile, setPreviewImageFile] = useState<File | null>(null);
  const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

  const [showVideoPreview, setShowVideoPreview] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
    reset,
  } = useForm<VideoFormData>({
    defaultValues: {
      title: video?.title || '',
      description: video?.description || '',
      category: (video?.category as any) || 'longvideo',
      youtubeUrl: video?.youtubeUrl || '',
      thumbnailFile: null,
      thumbnailUrl: video?.thumbnailUrl || '',
    },
  });

  const watchedYoutubeUrl = watch('youtubeUrl');

  useEffect(() => {
    if (video) {
      setDraftId(video.id);
      isDraftCreatedRef.current = true;
      setThumbnailUrl(video.thumbnailUrl || '');
      reset({
        draftId: video.id,
        title: video.title || '',
        description: video.description || '',
        category: video.category,
        youtubeUrl: video.youtubeUrl || '',
        thumbnailFile: null,
        thumbnailUrl: video.thumbnailUrl || '',
      });
      return;
    }

    const createDraft = async () => {
      if (!draftId && !isDraftCreatedRef.current) {
        try {
          const result = await videoService.createDraft();
          const id = result.data?.docId || '';
          setDraftId(id);
          setValue('draftId', id);
          isDraftCreatedRef.current = true;
        } catch (e) {
          console.error('Failed to create video draft', e);
        }
      }
    };

    createDraft();
  }, [video, draftId, reset, setValue]);

  const handleThumbnailUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPreviewImageFile(file);
    const url = URL.createObjectURL(file);
    setPreviewImageUrl(url);
    setShowImagePreview(true);
  };

  const confirmImageUpload = async () => {
    if (!previewImageFile || !draftId) return;
    setThumbnailUploading(true);
    try {
      const downloadURL = await videoService.uploadThumbnail(draftId, previewImageFile);
      await videoService.updateDraft(draftId, { thumbnailUrl: downloadURL });
      setThumbnailUrl(downloadURL);
      setValue('thumbnailUrl', downloadURL);
      setShowImagePreview(false);
      setPreviewImageFile(null);
      if (previewImageUrl) URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl('');
    } catch (e) {
      console.error('Failed to upload thumbnail', e);
    } finally {
      setThumbnailUploading(false);
    }
  };

  const cancelImageUpload = () => {
    setShowImagePreview(false);
    setPreviewImageFile(null);
    if (previewImageUrl) {
      URL.revokeObjectURL(previewImageUrl);
      setPreviewImageUrl('');
    }
    const fileInput = document.getElementById('thumbnail-upload') as HTMLInputElement;
    if (fileInput) fileInput.value = '';
  };

  const removeThumbnail = async () => {
    try {
      if (thumbnailUrl) {
        await videoService.deleteThumbnailFromStorage(thumbnailUrl);
      }
    } catch {
      // ignore
    } finally {
      setThumbnailUrl('');
      setValue('thumbnailUrl', '');
      const fileInput = document.getElementById('thumbnail-upload') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    }
  };

  const onSubmitWithValidation = async (data: VideoFormData) => {
    if (!draftId) return;

    const payload: Partial<VideoDoc> = {
      id: draftId,
      title: data.title,
      description: data.description,
      category: data.category,
      youtubeUrl: data.youtubeUrl,
      thumbnailUrl: data.thumbnailUrl || thumbnailUrl || '',
      status: 'published',
      isPublished: true,
      isDraft: false,
      featured: false,
    } as any;

    try {
      if (video) {
        await videoService.updateVideo(video.id, {
          title: data.title,
          description: data.description,
          category: data.category,
          youtubeUrl: data.youtubeUrl,
          thumbnailUrl: data.thumbnailUrl || thumbnailUrl || '',
          status: 'published',
        } as any);
      } else {
        await videoService.publishDraft(draftId, payload);
      }
      onSubmit(true);
    } catch (e) {
      console.error('Failed to save video', e);
      throw e;
    }
  };

  const canPreview = useMemo(() => Boolean(watchedYoutubeUrl && watchedYoutubeUrl.trim().length > 0), [watchedYoutubeUrl]);

  return (
    <form onSubmit={handleSubmit(onSubmitWithValidation)} className="space-y-6">
      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Basic Information</h3>
        </div>
        <div className="card-body space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Title *</label>
              <input
                type="text"
                className={`input ${errors.title ? 'input-error' : ''}`}
                disabled={readOnly}
                {...register('title', { required: 'Title is required', minLength: { value: 3, message: 'Min 3 characters' } })}
              />
              {errors.title && <p className="mt-1 text-sm text-error-600">{String(errors.title.message)}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Category *</label>
              <select
                className={`input ${errors.category ? 'input-error' : ''}`}
                disabled={readOnly}
                {...register('category', { required: 'Category is required' })}
              >
                <option value="longvideo">Long Video</option>
                <option value="short">Short</option>
              </select>
              {errors.category && <p className="mt-1 text-sm text-error-600">{String(errors.category.message)}</p>}
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
            <textarea
              rows={4}
              className={`input ${errors.description ? 'input-error' : ''}`}
              disabled={readOnly}
              {...register('description', { required: 'Description is required', minLength: { value: 10, message: 'Min 10 characters' } })}
            />
            {errors.description && <p className="mt-1 text-sm text-error-600">{String(errors.description.message)}</p>}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">YouTube URL *</label>
              <input
                type="url"
                className={`input ${errors.youtubeUrl ? 'input-error' : ''}`}
                disabled={readOnly}
                placeholder="https://www.youtube.com/watch?v=..."
                {...register('youtubeUrl', {
                  required: 'YouTube URL is required',
                  validate: (v) => (videoService as any).normalizeYouTubeUrl(v) ? true : 'Invalid URL',
                })}
              />
              {errors.youtubeUrl && <p className="mt-1 text-sm text-error-600">{String(errors.youtubeUrl.message)}</p>}
            </div>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                className="btn btn-secondary"
                disabled={!canPreview}
                onClick={() => setShowVideoPreview(true)}
              >
                <Play className="w-4 h-4 mr-2" />
                Preview
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <h3 className="text-lg font-semibold text-gray-900">Thumbnail</h3>
        </div>
        <div className="card-body space-y-6">
          {thumbnailUrl ? (
            <div className="relative inline-block">
              <img
                src={thumbnailUrl}
                alt="Thumbnail preview"
                className="w-48 h-28 object-cover rounded-apple border border-gray-200"
              />
              {!readOnly && (
                <button
                  type="button"
                  onClick={removeThumbnail}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-error-500 text-white rounded-full flex items-center justify-center hover:bg-error-600"
                >
                  <X className="w-4 h-4" />
                </button>
              )}
            </div>
          ) : (
            <div className="border-2 border-dashed border-gray-300 rounded-apple p-6 text-center">
              <Image className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-sm text-gray-600 mb-2">Upload thumbnail image</p>
              <input
                type="file"
                accept="image/*"
                onChange={handleThumbnailUpload}
                className="hidden"
                id="thumbnail-upload"
                disabled={readOnly}
              />
              <label
                htmlFor="thumbnail-upload"
                className={`btn btn-secondary cursor-pointer ${thumbnailUploading ? 'opacity-50 cursor-not-allowed' : ''} ${readOnly ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                <Upload className="w-4 h-4 mr-2" />
                {thumbnailUploading ? 'Uploading...' : 'Choose File'}
              </label>
            </div>
          )}
        </div>
      </div>

      <div className="flex justify-end space-x-4">
        <button
          type="button"
          onClick={async () => {
            try {
              if (draftId && !video) {
                await videoService.deleteDraftDocOnly(draftId);
              }
            } catch {
              // ignore cleanup errors
            } finally {
              onCancel();
            }
          }}
          className="btn btn-secondary"
        >
          Cancel
        </button>
        {!readOnly && (
          <button type="submit" disabled={loading} className="btn btn-primary">
            {loading ? 'Saving...' : video ? 'Update Video' : 'Create Video'}
          </button>
        )}
      </div>

      <ImagePreviewModal
        isOpen={showImagePreview}
        imageUrl={previewImageUrl}
        fileName={previewImageFile?.name || ''}
        onConfirm={confirmImageUpload}
        onCancel={cancelImageUpload}
        isUploading={thumbnailUploading}
      />

      <VideoPreviewModal
        isOpen={showVideoPreview}
        title={watch('title') || 'Video'}
        youtubeUrl={watch('youtubeUrl') || ''}
        onCancel={() => setShowVideoPreview(false)}
        onConfirm={() => setShowVideoPreview(false)}
        showActions={false}
      />
    </form>
  );
};

export default VideoForm;

